import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import { resumeAPI } from '../services/api';
import toast from 'react-hot-toast';

export default function UploadCV() {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const navigate = useNavigate();

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragActive(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && (droppedFile.name.endsWith('.pdf') || droppedFile.name.endsWith('.docx'))) {
      setFile(droppedFile);
    } else {
      toast.error('Please upload a PDF or DOCX file');
    }
  }, []);

  const handleFileSelect = (e) => {
    if (e.target.files[0]) setFile(e.target.files[0]);
  };

  const handleUpload = async () => {
    if (!file) return toast.error('Please select a file');
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const { data } = await resumeAPI.upload(formData);
      setParsed(data.resume);
      toast.success('CV parsed successfully!');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to upload CV');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-layout">
      <Sidebar />
      <main className="main-content">
        <div className="page-header">
          <div className="page-title">Upload Your CV</div>
          <div className="page-subtitle">Upload your resume and our AI will analyze it to generate tailored interview questions</div>
        </div>

        {!parsed ? (
          <div className="card card-lg">
            <div
              className={`upload-zone ${dragActive ? 'active' : ''}`}
              onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
              onDragLeave={() => setDragActive(false)}
              onDrop={handleDrop}
              onClick={() => document.getElementById('file-input').click()}
            >
              <div className="upload-icon">📄</div>
              {file ? (
                <>
                  <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>{file.name}</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>
                    {(file.size / 1024 / 1024).toFixed(2)} MB — Ready to upload
                  </p>
                </>
              ) : (
                <>
                  <p style={{ fontSize: 16, fontWeight: 500, marginBottom: 4 }}>Drop your CV here or click to browse</p>
                  <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>Supports PDF and DOCX (max 10MB)</p>
                </>
              )}
              <input id="file-input" type="file" accept=".pdf,.docx,.doc" onChange={handleFileSelect} style={{ display: 'none' }} />
            </div>

            {file && (
              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'center' }}>
                <button className="btn btn-secondary" onClick={() => { setFile(null); setParsed(null); }}>Cancel</button>
                <button className="btn btn-primary btn-lg" onClick={handleUpload} disabled={loading}>
                  {loading ? <><span className="spinner" /> Analyzing CV...</> : 'Upload & Analyze'}
                </button>
              </div>
            )}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div className="card" style={{ borderLeft: '4px solid var(--success)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 20 }}>✅</span>
                <span style={{ fontWeight: 600, fontSize: 16 }}>CV Analyzed Successfully</span>
              </div>
              <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{parsed.summary}</p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
              <div className="card">
                <div className="card-title" style={{ marginBottom: 12 }}>Skills Detected</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(parsed.skills || []).map((skill, i) => (
                    <span key={i} className="tag tag-primary">{skill}</span>
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="card-title" style={{ marginBottom: 12 }}>Suggested Roles</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                  {(parsed.suggestedRoles || []).map((role, i) => (
                    <span key={i} className="tag tag-success">{role}</span>
                  ))}
                </div>
              </div>
            </div>

            <div className="card">
              <div className="card-title" style={{ marginBottom: 12 }}>Experience</div>
              {(parsed.experience || []).map((exp, i) => (
                <div key={i} style={{ marginBottom: 16, paddingBottom: 16, borderBottom: i < parsed.experience.length - 1 ? '1px solid var(--border)' : 'none' }}>
                  <div style={{ fontWeight: 600 }}>{exp.title}</div>
                  <div style={{ fontSize: 14, color: 'var(--text-secondary)' }}>{exp.company} — {exp.duration}</div>
                  <p style={{ fontSize: 14, marginTop: 4 }}>{exp.description}</p>
                </div>
              ))}
            </div>

            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button className="btn btn-secondary" onClick={() => { setFile(null); setParsed(null); }}>Upload Another</button>
              <button className="btn btn-primary btn-lg" onClick={() => navigate('/interview/setup', { state: { resumeId: parsed.id } })}>
                Start Interview →
              </button>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
